import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import styles from './Chart.module.css';
import { ComposedChart, Dot, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Line, Rectangle, Legend, Customized, ReferenceArea, ReferenceLine, Bar, Cell } from 'recharts';
import { BloodPressureIcon } from '../../../components/icons/Icons';
import { useSelector, useDispatch } from 'react-redux';
import { getGraphXValue } from '../../../utils/dateUtils';
import { getAlertColors } from '../../../utils/alertUtils';
import { sortData, buildDeviceColorMap } from '../../../utils/graphUtils';
import VitalCardHeader from './components/VitalCardHeader';
import { formatDateWithTime } from '../../../utils/dateUtils';
import { openModal, closeModal } from '../../../components/PerinHealthModelSlice';
import PerinHealthModal from '../../../components/PerinHealthModel';
import useAxios from '../../../services/AxiosConfig';
import { showAlert } from '../../../components/PerinHealthAlertSlice';
/**
 * BP-only: use the actual calibration bucket. Do not snap to a nearby reading,
 * otherwise a 16:48 calibration can render on a 14:46 BP timestamp.
 */
function resolveBpCalibrationLineCategory(calibrationIso, dateFormat, averageGrouped, readingInterval) {
  const key = getGraphXValue(calibrationIso, dateFormat, averageGrouped, readingInterval);
  return calibrationIso && key ? key : null;
}

const renderCalibrationLabel = ({ viewBox }) => {
  if (!viewBox || typeof viewBox.x !== 'number' || !Number.isFinite(viewBox.x)) return null;

  const fill = '#b8860b';
  const text = 'calibration';
  const rw = Math.max(text.length * 7.5 + 14, 86);
  const rh = 20;
  const labelY = (viewBox.y ?? 0) - 15;

  return (
    <g style={{ pointerEvents: 'none' }}>
      <g transform={`translate(${viewBox.x},${labelY})`}>
        <rect
          x={-rw / 2}
          y={-rh / 2}
          width={rw}
          height={rh}
          rx={4}
          fill="rgba(255,255,255,0.96)"
          stroke={fill}
          strokeWidth={1}
        />
        <text x={0} y={5} textAnchor="middle" fill={fill} fontSize={12} fontWeight={700}>
          {text}
        </text>
      </g>
    </g>
  );
}

function isCalibrationOverdue(entry) {
  if (!entry) return false;
  const flag =
    entry.calibrationOverdue ??
    entry.calibration_overdue ??
    entry.isCalibrationOverdue ??
    entry.is_calibration_overdue;
  return flag === true || flag === 1 || flag === 'true';
}

function stripBpReading(entry) {
  return {
    ...entry,
    systolic: null,
    diastolic: null,
    bpRange: null,
    'systolic-range': null,
    'diastolic-range': null,
  };
}

function withBpRange(entry) {
  if (
    isCalibrationOverdue(entry) ||
    entry?.systolic == null ||
    entry?.diastolic == null ||
    Number.isNaN(Number(entry.systolic)) ||
    Number.isNaN(Number(entry.diastolic))
  ) {
    return { ...entry, bpRange: null };
  }

  return { ...entry, bpRange: [entry.diastolic, entry.systolic] };
}

const OUTSIDE_CALIBRATION_ALERT_DISPLAY = 'Patient initiated reading, outside of calibration period';
const BP_BAR_SIZE = 10;
const ALERT_BAR_SIZE = 1;
const BAR_GAP = 4;
const BP_BAR_CENTER_OFFSET = ((BP_BAR_SIZE + ALERT_BAR_SIZE + BAR_GAP) / 2) - (BP_BAR_SIZE / 2);
const ALERT_BAR_CENTER_OFFSET = (BP_BAR_SIZE + BAR_GAP + (ALERT_BAR_SIZE / 2)) - ((BP_BAR_SIZE + ALERT_BAR_SIZE + BAR_GAP) / 2);

function isOutsideCalibrationAlert(alert) {
  return alert?.alert_hover_display === OUTSIDE_CALIBRATION_ALERT_DISPLAY;
}

function isRealApiAlert(alert) {
  return Boolean(alert?.alert_hover_display) && !isOutsideCalibrationAlert(alert);
}

function isSameChartTimestamp(a, b) {
  if (!a || !b) return false;
  if (a.timeStamp && b.timeStamp && a.timeStamp === b.timeStamp) return true;
  if (a.sortTime != null && b.sortTime != null && a.sortTime === b.sortTime) return true;
  if (a.date && b.date && a.date === b.date) return true;
  return false;
}

function hasRealAlertAtSameTimestamp(entries, entry) {
  return entries.some(
    (other) => other !== entry && isRealApiAlert(other.alert) && isSameChartTimestamp(other, entry)
  );
}

const BloodPressureCardView = React.forwardRef(({ className, healthEvaluationData, vitalName, alertsForPeriod, handlePointClick }, ref) => {
  const chartContainerRef = useRef(null);
  const [data, setData] = useState([]);
  const [displayData, setDisplayData] = useState({});
  const { startDate, endDate, averageGrouped, readingInterval } = useSelector((state) => state.patientDetailFilter);
  const vitals = useSelector((state) => state.vital.modalities[vitalName]);
  const { dateFormat } = useSelector((state) => state.userInfo);
  const patient = useSelector((state) => state.patient);
  const dispatch = useDispatch();
  const axiosConfig = useSelector((state) => state.axiosConfig);
  const axios = useAxios(axiosConfig.envURL);

  const [opacity, setOpacity] = useState({});

  const handleLegendClick = (deviceName) => {
    if (!deviceName) return;
    setOpacity((op) => {
      const visible = op[deviceName] !== 0;
      return { ...op, [deviceName]: visible ? 0 : 1 };
    });
  };

  const getLatestMeasurement = useCallback((healthEvaluationData) => {
    const lastCalibrationDate = patient?.lastCalibrationDate ? formatDateWithTime(patient.lastCalibrationDate, dateFormat) : 'N/A';
    if (!healthEvaluationData || healthEvaluationData.length === 0) {
      return { measurement: 'No Data', latestDate: 'N/A', lastCalibrationDate: lastCalibrationDate };
    }


    const deviceNamesData = healthEvaluationData.filter(
      (entry) => entry.systolic !== null && !isCalibrationOverdue(entry)
    );
    if (deviceNamesData.length === 0) {
      return {
        measurement: 'No Data',
        latestDate: averageGrouped ? 'Interval Average' : 'Latest: N/A',
        lastCalibrationDate: lastCalibrationDate
      };
    }
    if (averageGrouped) {
      const totalSystolic = deviceNamesData.reduce((sum, entry) => sum + entry.systolic, 0);
      const totalDiastolic = deviceNamesData.reduce((sum, entry) => sum + entry.diastolic, 0);

      const averageSystolic = totalSystolic / deviceNamesData.length;
      const averageDiastolic = totalDiastolic / deviceNamesData.length;
      return {
        measurement: `${averageSystolic.toFixed(1)}/${averageDiastolic.toFixed(1)} mmHG`,
        latestDate: 'Interval Average',
        lastCalibrationDate: lastCalibrationDate
      };
    } else {
      let outOfRangeCount = 0;

      if ((vitals?.systolicUpperLimit !== undefined && vitals?.systolicUpperLimit !== 0) || (vitals?.systolicLowerLimit !== undefined && vitals?.systolicLowerLimit !== 0) || (vitals?.diastolicUpperLimit !== undefined && vitals?.diastolicUpperLimit !== 0) || (vitals?.diastolicLowerLimit !== undefined && vitals?.diastolicLowerLimit !== 0)) {
        const outOfRange = deviceNamesData.filter((entry) => {
          const systolic = entry.systolic;
          const diastolic = entry.diastolic;
          if (systolic > vitals?.systolicUpperLimit || systolic < vitals?.systolicLowerLimit || diastolic > vitals?.diastolicUpperLimit || diastolic < vitals?.diastolicLowerLimit) {
            return true;
          }
        });
        outOfRangeCount = outOfRange.length;
      }

      const latestMeasurement = deviceNamesData.reduce((latest, entry) => {
        const dateA = new Date(latest.date);
        const dateB = new Date(entry.date);
        return dateB > dateA ? entry : latest;
      }, deviceNamesData[0]);

      return {
        measurement: (latestMeasurement?.systolic !== undefined && latestMeasurement?.diastolic !== undefined)
          ? `${Math.round(latestMeasurement?.systolic)}/${Math.round(latestMeasurement?.diastolic)} mmHG`
          : 'No Data',
        latestDate: 'Latest: ' + (latestMeasurement?.date || 'N/A'),
        lastCalibrationDate: lastCalibrationDate,
        outOfRangeCount: outOfRangeCount
      };
    }
  }, [averageGrouped, patient?.lastCalibrationDate, dateFormat, vitals]);

  const updateData = useCallback(() => {
    const transformedData = healthEvaluationData
      .filter((entry) =>
        entry.systolic != null ||
        entry.diastolic != null ||
        isCalibrationOverdue(entry)
      )
      .map((entry) => (isCalibrationOverdue(entry) ? stripBpReading(entry) : withBpRange(entry)))
      .sort(sortData);
    setData(transformedData);
    setDisplayData(getLatestMeasurement(transformedData));
  }, [healthEvaluationData, getLatestMeasurement]);

  useEffect(() => {
    updateData();
  }, [updateData, startDate, endDate]);

  const deviceNames = Array.from(new Set(healthEvaluationData.map(entry => entry.deviceName)));
  const deviceNamesData = deviceNames.filter(deviceName =>
    healthEvaluationData.some(
      (entry) =>
        entry.deviceName === deviceName &&
        entry.systolic !== null &&
        entry.diastolic !== null &&
        !isCalibrationOverdue(entry)
    )
  );

  const legendDeviceNames = useMemo(
    () => [...deviceNamesData].sort((a, b) => String(a).localeCompare(String(b))),
    [deviceNamesData.join('\u0001')]
  );

  const deviceColorByName = useMemo(() => buildDeviceColorMap(deviceNamesData), [deviceNamesData.join('\u0001')]);

  const legendPayload = useMemo(
    () =>
      legendDeviceNames.map((name) => ({
        id: String(name),
        value: name,
        type: 'square',
        color: deviceColorByName[name],
      })),
    [legendDeviceNames, deviceColorByName]
  );

  useEffect(() => {
    setOpacity({});
  }, [legendDeviceNames.join('\u0001')]);

  const ScatterDot = (props) => {
    const { cx, cy, payload } = props;
    if (
      isCalibrationOverdue(payload) ||
      payload?.systolic == null ||
      payload?.diastolic == null
    ) {
      return null;
    }
    return <Dot cx={cx} cy={cy} fill="#828282" r={2} />;
  };

  const getBpBarColors = (payload) => {
    const deviceTint = deviceColorByName[payload?.deviceName] ?? '#7A6B78';
    let fillColor = deviceTint;
    let stroke = '#7A6B78';

    if ((payload?.systolic_adhoc === true || payload?.diastolic_adhoc === true) && !averageGrouped) {
      fillColor = '#B8860B';
      stroke = '#B8860B';
    }

    const systolicUpperLimit = vitals?.systolicUpperLimit ?? 0;
    const systolicLowerLimit = vitals?.systolicLowerLimit ?? 0;
    const diastolicUpperLimit = vitals?.diastolicUpperLimit ?? 0;
    const diastolicLowerLimit = vitals?.diastolicLowerLimit ?? 0;

    if ((systolicUpperLimit !== 0 && payload?.systolic > systolicUpperLimit) ||
      (systolicLowerLimit !== 0 && payload?.systolic < systolicLowerLimit) ||
      (diastolicUpperLimit !== 0 && payload?.diastolic > diastolicUpperLimit) ||
      (diastolicLowerLimit !== 0 && payload?.diastolic < diastolicLowerLimit)) {
      fillColor = '#EBB6B6';
      stroke = '#333';
    }

    return { fillColor, stroke };
  };

  const BpRangeBar = (props) => {
    const { x, y, width, height, payload } = props;
    if (
      isCalibrationOverdue(payload) ||
      !Array.isArray(payload?.bpRange) ||
      payload.bpRange.length !== 2 ||
      payload.systolic == null ||
      payload.diastolic == null ||
      !Number.isFinite(x) ||
      !Number.isFinite(y) ||
      !Number.isFinite(width) ||
      !Number.isFinite(height)
    ) {
      return null;
    }

    const deviceName = payload.deviceName ?? '';
    if (opacity[deviceName] === 0) return null;

    const { fillColor, stroke } = getBpBarColors(payload);
    const barWidth = BP_BAR_SIZE;

    return (
      <Rectangle
        width={barWidth}
        height={Math.abs(height)}
        x={x + (width - barWidth) / 2 + BP_BAR_CENTER_OFFSET}
        y={Math.min(y, y + height)}
        fill={fillColor}
        stroke={stroke}
        strokeWidth={1}
        onClick={() => handlePointClick(deviceName, payload, vitals)}
        style={{ pointerEvents: 'visible' }}
      />
    );
  };

  const BloodPressureTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const barData = payload.find(p => p.dataKey === 'alertY');
      const filteredPayload = payload.filter(
        (entry) => !entry.dataKey.includes('-range') && !entry.dataKey.includes('alertY') && entry.dataKey !== 'bpRange'
      );
      const rowPayload = filteredPayload.find((entry) => entry.payload)?.payload ?? barData?.payload;
      const hasBpReading =
        rowPayload &&
        !isCalibrationOverdue(rowPayload) &&
        rowPayload.systolic != null &&
        rowPayload.diastolic != null &&
        typeof rowPayload.systolic === 'number' &&
        typeof rowPayload.diastolic === 'number' &&
        filteredPayload.some(
          (entry) =>
            (entry.dataKey === 'systolic' || entry.dataKey === 'diastolic') &&
            entry.value != null
        );
      const showBpBlock = hasBpReading;
      const alertOnRow = barData?.payload?.alert;
      const showAlertContent = Boolean(alertOnRow?.alert_hover_display);
      if (!showBpBlock && !showAlertContent) return null;
      return (
        <div className={styles.PerinTooltip}>
          {showBpBlock &&
            <>
              {(rowPayload[`systolic_adhoc`] === true || rowPayload[`diastolic_adhoc`] === true) && !averageGrouped &&
                <p style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "10px", fontWeight: "700", color: "#F5C10D" }}>{`Patient Initiated`}</p>
              }
              {averageGrouped ? (
                <p style={{ fontSize: "20px" }}>{`Avg: ${rowPayload.systolic.toFixed(0)}/${rowPayload.diastolic.toFixed(0)} mmHG`}</p>
              ) : (
                <p style={{ fontSize: "20px" }}>{`${rowPayload.systolic.toFixed(0)}/${rowPayload.diastolic.toFixed(0)} mmHG`}</p>
              )}
              {averageGrouped && rowPayload[`systolic-range`] &&
                <p style={{ fontSize: "16px" }}>{`Systolic min/max: ${rowPayload[`systolic-range`]?.min.toFixed(0)}, ${rowPayload[`systolic-range`]?.max.toFixed(0)}`}</p>
              }
              {averageGrouped && rowPayload[`diastolic-range`] &&
                <p style={{ fontSize: "16px" }}>{`Diastolic min/max: ${rowPayload[`diastolic-range`]?.min.toFixed(0)}, ${rowPayload[`diastolic-range`]?.max.toFixed(0)}`}</p>
              }
              <p style={{ fontSize: "16px", paddingTop: "5px" }}>{`${label}`}</p>
              {rowPayload.deviceName &&
                <p style={{ fontSize: "12px", fontWeight: "700" }}>{`${rowPayload.deviceName}`}</p>
              }
              {((vitals?.systolicUpperLimit !== 0 && vitals?.systolicUpperLimit !== undefined) ||
                (vitals?.systolicLowerLimit !== 0 && vitals?.systolicLowerLimit !== undefined) ||
                (vitals?.diastolicUpperLimit !== 0 && vitals?.diastolicUpperLimit !== undefined) ||
                (vitals?.diastolicLowerLimit !== 0 && vitals?.diastolicLowerLimit !== undefined)) &&
                <hr style={{ margin: "0" }}></hr>
              }
              {(vitals?.systolicUpperLimit !== 0 && vitals?.systolicUpperLimit !== undefined) &&
                <p style={{ fontSize: "12px", fontWeight: "700" }}>{`Systolic upper limit: ${vitals?.systolicUpperLimit}`}</p>
              }
              {(vitals?.systolicLowerLimit !== 0 && vitals?.systolicLowerLimit !== undefined) &&
                <p style={{ fontSize: "12px", fontWeight: "700" }}>{`Systolic lower limit: ${vitals?.systolicLowerLimit}`}</p>
              }
              {(vitals?.diastolicUpperLimit !== 0 && vitals?.diastolicUpperLimit !== undefined) &&
                <p style={{ fontSize: "12px", fontWeight: "700" }}>{`Diastolic upper limit: ${vitals?.diastolicUpperLimit}`}</p>
              }
              {(vitals?.diastolicLowerLimit !== 0 && vitals?.diastolicLowerLimit !== undefined) &&
                <p style={{ fontSize: "12px", fontWeight: "700" }}>{`Diastolic lower limit: ${vitals?.diastolicLowerLimit}`}</p>
              }
            </>
          }
          {showAlertContent &&
            <>
              {showBpBlock && <hr style={{ margin: "0" }}></hr>}
              {(() => {
                const alertCreatedDate = barData.payload.alert?.createdDate;
                const alertDateLabel = alertCreatedDate
                  ? formatDateWithTime(alertCreatedDate, dateFormat)
                  : barData.payload.date;
                return alertDateLabel
                  ? <p style={{ fontSize: "12px", fontWeight: "700" }}>{alertDateLabel}</p>
                  : null;
              })()}
              <p style={{ fontSize: "12px", fontWeight: "700" }}>{`${barData.payload.alert?.alert_hover_display}`}</p>
            </>
          }
        </div>
      );
    }

    return null;
  };

  const hasVisibleBpReadings = data.some(
    (entry) =>
      !isCalibrationOverdue(entry) &&
      entry.systolic != null &&
      entry.diastolic != null &&
      !Number.isNaN(Number(entry.systolic)) &&
      !Number.isNaN(Number(entry.diastolic))
  );
  const hasCalibrationOverdueReadings = data.some(isCalibrationOverdue);
  const isCalibrationAlertOnlyView = !hasVisibleBpReadings && hasCalibrationOverdueReadings;

  const calculateDomain = () => {
    if (isCalibrationAlertOnlyView) {
      return [0, 1];
    }

    const systolicUpperLimit = vitals?.systolicUpperLimit ?? 0;
    const systolicLowerLimit = vitals?.systolicLowerLimit ?? 0;
    const diastolicUpperLimit = vitals?.diastolicUpperLimit ?? 0;
    const diastolicLowerLimit = vitals?.diastolicLowerLimit ?? 0;

    // Start with the limits - minDomain should be the lowest limit, maxDomain should be the highest limit
    let minDomain = diastolicLowerLimit || systolicLowerLimit || null;
    let maxDomain = systolicUpperLimit || diastolicUpperLimit || null;

    let highestSystolic = null;
    let lowestDiastolic = null;

    data.forEach(entry => {
      if (isCalibrationOverdue(entry)) return;

      if (entry["systolic"] !== null &&
        !isNaN(entry["systolic"]) && entry["diastolic"] !== null && !isNaN(entry["diastolic"])) {
        if (highestSystolic === null || entry["systolic"] > highestSystolic) {
          highestSystolic = entry["systolic"];
        }
        if (lowestDiastolic === null || entry["diastolic"] < lowestDiastolic) {
          lowestDiastolic = entry["diastolic"];
        }
      }
    });

    // Adjust domain based on actual data
    if (lowestDiastolic !== null) {
      if (minDomain === null || lowestDiastolic < minDomain) {
        minDomain = lowestDiastolic;
      }
    }

    if (highestSystolic !== null) {
      if (maxDomain === null || highestSystolic > maxDomain) {
        maxDomain = highestSystolic;
      }
    }

    // Fallback if no limits or data
    if (minDomain === null) minDomain = 0;
    if (maxDomain === null) maxDomain = 200;

    const bufferPercentage = 0.2;
    const range = maxDomain - minDomain;
    const buffer = range * bufferPercentage;
    const newMin = minDomain - buffer < 0 ? minDomain : minDomain - buffer;

    return [newMin, maxDomain + buffer];
  };

  const domain = calculateDomain();

  const generateTicks = () => {
    if (isCalibrationAlertOnlyView) {
      return [];
    }

    const systolicUpperLimit = vitals?.systolicUpperLimit ?? 0;
    const systolicLowerLimit = vitals?.systolicLowerLimit ?? 0;
    const diastolicUpperLimit = vitals?.diastolicUpperLimit ?? 0;
    const diastolicLowerLimit = vitals?.diastolicLowerLimit ?? 0;

    let ticks = [...domain];
    if (systolicLowerLimit !== 0) {
      ticks.push(systolicLowerLimit);
    }
    if (diastolicLowerLimit !== 0) {
      ticks.push(diastolicLowerLimit);
    }
    if (vitals?.targetValue !== 0) {
      ticks.push(vitals?.targetValue);
    }
    if (systolicUpperLimit !== 0) {
      ticks.push(systolicUpperLimit);
    }
    if (diastolicUpperLimit !== 0) {
      ticks.push(diastolicUpperLimit);
    }

    const uniqueTicks = Array.from(new Set(ticks.filter(tick => tick !== undefined && tick !== null))).sort((a, b) => a - b);
    return uniqueTicks.map((item) => parseFloat(item.toFixed(1)))
  };

  const ticks = generateTicks();

  const CustomizedMinMaxRectangle = ({ formattedGraphicalItems }) => {
    const minSeries = formattedGraphicalItems?.find(
      (it) => it?.props?.dataKey === 'diastolic-range.min' && Array.isArray(it.props?.points)
    );
    const maxSeries = formattedGraphicalItems?.find(
      (it) => it?.props?.dataKey === 'systolic-range.max' && Array.isArray(it.props?.points)
    );

    return minSeries?.props?.points?.map((firstSeriesPoint, index) => {
      const secondSeriesPoint = maxSeries?.props?.points?.[index];
      if (!firstSeriesPoint || !secondSeriesPoint || !firstSeriesPoint.payload) return null;

      if (
        isCalibrationOverdue(firstSeriesPoint.payload) ||
        !Number.isFinite(firstSeriesPoint.y) ||
        !Number.isFinite(secondSeriesPoint.y)
      ) {
        return null;
      }

      const dn = firstSeriesPoint?.payload?.deviceName ?? '';
      if (opacity[dn] === 0) return null;

      const yDifference = Math.abs(firstSeriesPoint.y - secondSeriesPoint.y);
      const yPosition = Math.min(firstSeriesPoint.y, secondSeriesPoint.y);
      const deviceTint = deviceColorByName[dn] ?? 'lightgrey';
      const fillColor = deviceTint;

      const key = `${firstSeriesPoint.payload.name}-${index}-${firstSeriesPoint.y}-${secondSeriesPoint.y}`;

      return (
        <Rectangle
          key={key}
          width={1}
          height={yDifference}
          x={secondSeriesPoint.x + 4}
          y={yPosition + 5}
          fill={fillColor}
          stroke={fillColor}
          strokeWidth={1}
          style={{ cursor: 'none' }}
        />
      );
    });
  };

  const { chartData: sortedDataWithAlerts, calibrationLineX: bpCalibrationLineX } = useMemo(() => {
    const alertsForModality = alertsForPeriod.filter(
      (alert) => alert.alert_modalityId === 5 || alert.alert_modalityId === 10
    );

    let updatedData = [...data];
    const calibrationOverdueRows = updatedData.filter(isCalibrationOverdue);

    const alertData = alertsForModality
      .map(alert => ({
        date: getGraphXValue(alert.createdDate, dateFormat, averageGrouped, readingInterval),
        timeStamp: alert.createdDate,
        sortTime: new Date(alert.createdDate).getTime(),
        alertY: domain[1],
        alert: alert
      }))
      .filter((alertItem) =>
        !calibrationOverdueRows.some((entry) => isSameChartTimestamp(alertItem, entry))
      );

    alertData.forEach(alertItem => {
      let matchIndex = updatedData.findIndex(item => item.date === alertItem.date);
      if (matchIndex === -1 && alertItem.alert?.createdDate) {
        const alertMs = new Date(alertItem.alert.createdDate).getTime();
        if (!Number.isNaN(alertMs)) {
          matchIndex = updatedData.findIndex(
            (item) => item.sortTime != null && item.sortTime === alertMs
          );
        }
      }
      if (matchIndex !== -1) {
        updatedData[matchIndex] = { ...updatedData[matchIndex], alertY: alertItem.alertY, alert: alertItem.alert };
      } else {
        updatedData.push(alertItem);
      }
    });

    updatedData = updatedData.map((entry) => {
      if (!isCalibrationOverdue(entry)) return entry;

      const stripped = stripBpReading(entry);
      if (isCalibrationOverdue(entry)) {
        return {
          ...stripped,
          alertY: domain[1],
          alert: {
            alert_hover_display: OUTSIDE_CALIBRATION_ALERT_DISPLAY,
            alert_color: 'yellow',
            createdDate: entry.timeStamp,
          },
        };
      }

      const hasRealAlertOnRow = isRealApiAlert(stripped.alert);
      const hasRealAlertNearby = hasRealAlertAtSameTimestamp(updatedData, entry);

      if (hasRealAlertOnRow) {
        return stripped;
      }

      if (hasRealAlertNearby) {
        return { ...stripped, alertY: undefined, alert: undefined };
      }

      return {
        ...stripped,
        alertY: domain[1],
        alert: {
          alert_hover_display: OUTSIDE_CALIBRATION_ALERT_DISPLAY,
          alert_color: 'yellow',
          createdDate: entry.timeStamp,
        },
      };
    });

    let calibrationLineX = null;
    if (patient?.lastCalibrationDate) {
      const calMs = new Date(patient.lastCalibrationDate).getTime();
      const startMs = startDate ? new Date(startDate).getTime() : null;
      const endMs = endDate ? new Date(endDate).getTime() : null;
      const inSelectedRange =
        Number.isFinite(calMs) &&
        (startMs === null || calMs >= startMs) &&
        (endMs === null || calMs <= endMs);

      if (inSelectedRange) {
        calibrationLineX = resolveBpCalibrationLineCategory(
          patient.lastCalibrationDate,
          dateFormat,
          averageGrouped,
          readingInterval
        );
        if (
          calibrationLineX &&
          !updatedData.some((item) => item.date === calibrationLineX)
        ) {
          updatedData.push({
            date: calibrationLineX,
            sortTime: calMs,
            timeStamp: patient.lastCalibrationDate,
            systolic: null,
            diastolic: null,
          });
        }
      }
    }

    updatedData.sort(sortData);

    return { chartData: updatedData, calibrationLineX };
  }, [
    data,
    alertsForPeriod,
    vitals?.modalityId,
    patient?.lastCalibrationDate,
    dateFormat,
    averageGrouped,
    readingInterval,
    domain,
    startDate,
    endDate,
  ]);

  /** X-axis category key for last calibration within the selected date range. */
  const calibrationReferenceX =
    bpCalibrationLineX && sortedDataWithAlerts.some((r) => r.date === bpCalibrationLineX)
      ? bpCalibrationLineX
      : null;

  const BarWithIcon = (props) => {
    const { fill, x, y, width, height, payload } = props;

    const value = payload.alertY;
    if (value == null || value === 0) {
      return null;
    }

    const barCenterX = x + width / 2 - ALERT_BAR_CENTER_OFFSET;
    const barTopY = y;
    const barBottomY = y + height;

    const lineWidth = width;

    const iconWidth = 16.6;
    const iconHeight = 14.6;

    return (
      <g>
        <rect
          x={barCenterX - lineWidth / 2}
          y={barTopY}
          width={lineWidth}
          height={height}
          fill={fill}
        />
        <svg
          x={barCenterX - iconWidth / 2}
          y={barBottomY - 9}
          width={iconWidth}
          height={iconHeight}
          viewBox="0 0 25 22"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect width="25" height="22" fill="white" />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M12.1536 1.16515C12.3076 0.898483 12.6925 0.898482 12.8465 1.16515L23.9718 20.4348C24.1258 20.7015 23.9333 21.0348 23.6254 21.0348H1.37472C1.0668 21.0348 0.874348 20.7015 1.02831 20.4348L12.1536 1.16515ZM11.3409 8.56895C11.3409 7.94615 11.8458 7.44127 12.4686 7.44127C13.0914 7.44127 13.5963 7.94615 13.5963 8.56895V14.2074C13.5963 14.8302 13.0914 15.3351 12.4686 15.3351C11.8458 15.3351 11.3409 14.8302 11.3409 14.2074V8.56895ZM12.4687 17.4009C11.8459 17.4009 11.341 17.9058 11.341 18.5286C11.341 19.1514 11.8459 19.6563 12.4687 19.6563C13.0915 19.6563 13.5964 19.1514 13.5964 18.5286C13.5964 17.9058 13.0915 17.4009 12.4687 17.4009Z"
            fill={fill}
          />
        </svg>
      </g>
    );
  };

  const triggerCalibration = () => {
    dispatch(
      openModal({
        activeModalId: "BpInitiate",
        header: 'Initiate Calibration?',
        message: `Are you sure you want to initiate a calibration? <br /><i>${patient?.lastCalibrationDate ? `Patient last calibrated on ${displayData.lastCalibrationDate}.` : 'Patient has not yet completed calibration.'}</i>`,
        buttons: [
          { label: 'Cancel', variant: 'secondary', onClickKey: 'NO' },
          { label: "Confirm", variant: 'primary', onClickKey: 'YES_CALIBRATION' },
        ],
      })
    );
  };

  const triggerReading = () => {
    dispatch(
      openModal({
        activeModalId: "BpInitiate",
        header: 'Initiate Reading?',
        message: `This will initiate a blood pressure reading for the patient.`,
        buttons: [
          { label: 'Cancel', variant: 'secondary', onClickKey: 'NO' },
          { label: "Confirm", variant: 'primary', onClickKey: 'YES_READING' },
        ],
      })
    );
  };

  const triggerCalibrationForPatient = async () => {
    // Calculate the next half hour mark from now
    const now = new Date();
    let minutes = now.getMinutes();
    let minutesToAdd = 0;

    if (minutes === 0 || minutes === 30) {
      minutesToAdd = 30 - minutes;
    } else if (minutes < 30) {
      minutesToAdd = 30 - minutes;
    } else {
      minutesToAdd = 60 - minutes;
    }

    const roundedDate = new Date(now.getTime() + minutesToAdd * 60000);
    roundedDate.setSeconds(0);
    roundedDate.setMilliseconds(0);

    const dueDate = roundedDate.toISOString();
    const payload = {
      accountId: patient.id,
      dueDate: dueDate,
      taskName: "Calibration",
      taskDescription: "Physician requested patient recalibrates"
    };
    try {
      const response = await axios.post('/tasks/calibrations', payload);
      if (response.data.data) {
        dispatch(showAlert({
          header: "Success",
          message: "Calibration initiated",
          type: 'success'
        }));
      }
    } catch (error) {
      console.error(error);
      dispatch(showAlert({
        header: "Error",
        message: `Failed to initiate: ${error.response.data.message}`,
        type: 'danger',
        dismissable: true
      }));
    }
  };


  const triggerReadingForPatient = async () => {

    const payload = {
      accountId: patient.id
    };
    try {
      const response = await axios.post('/tasks/create/bp', payload);
      if (response.data.data) {
        dispatch(showAlert({
          header: "Success",
          message: "Reading initiated",
          type: 'success'
        }));
      }
    } catch (error) {
      console.error(error);
      dispatch(showAlert({
        header: "Error",
        message: `Failed to initiate: ${error.response.data.message}`,
        type: 'danger',
        dismissable: true
      }));
    }
  };

  const handleDialogButtonClick = (onClickKey) => {
    if (onClickKey === 'YES_CALIBRATION') {
      dispatch(closeModal());
      triggerCalibrationForPatient();
    } else if (onClickKey === 'YES_READING') {
      dispatch(closeModal());
      triggerReadingForPatient();
    } else {
      dispatch(closeModal());
    }
  };

  const hasActivePatchBp = useMemo(
    () =>
      (patient?.carePrograms ?? []).some(
        (program) =>
          !program?.enrollment?.dischargeDate &&
          (program?.devices ?? []).some(
            (device) =>
              device?.deviceId === 1 &&
              (device?.modalityId === 5 || device?.modalityId === 10) &&
              (device?.frequency ?? 0) > 0
          )
      ),
    [patient?.carePrograms]
  );

  return (
    <div className={`${styles.cardContainer} ${className}`} ref={chartContainerRef}>
      <VitalCardHeader
        Icon={BloodPressureIcon}
        vitalName={"Blood Pressure"}
        measurement={displayData.measurement}
        latestDate={displayData.latestDate}
        outOfRangeCount={displayData.outOfRangeCount}
        lastCalibrationDate={displayData.lastCalibrationDate}
        triggerCalibration={hasActivePatchBp ? triggerCalibration : undefined}
        triggerReading={hasActivePatchBp ? triggerReading : undefined}
      />
      <div className={styles.chartWrapper} ref={ref}>
        <ResponsiveContainer width={"100%"} height={250}>
          <ComposedChart
            data={sortedDataWithAlerts}
            margin={{ bottom: 5 }}
            barGap={BAR_GAP}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" fontSize={12} />
            <YAxis
              domain={domain}
              ticks={ticks}
              tick={isCalibrationAlertOnlyView ? false : undefined}
              fontSize={12}
            />
            {legendPayload.length > 0 && (
              <Legend
                verticalAlign="top"
                align="right"
                height={30}
                iconType="square"
                wrapperStyle={{ fontSize: '14px', fontWeight: 'bold', width: '100%' }}
                payload={legendPayload}
                content={(props) => {
                  const items = props.payload;
                  if (!items?.length) return null;
                  const isSingleDevice = items.length <= 1;
                  return (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        flexWrap: 'wrap',
                        gap: '12px',
                        paddingRight: '4px',
                        fontSize: '14px',
                        fontWeight: 'bold',
                      }}
                    >
                      {items.map((entry) => {
                        const name = entry.value;
                        const hidden = !isSingleDevice && opacity[name] === 0;
                        const color = entry.color;
                        const swatch = (
                          <span
                            aria-hidden
                            style={{
                              width: '12px',
                              height: '12px',
                              flexShrink: 0,
                              backgroundColor: color,
                              borderRadius: '2px',
                            }}
                          />
                        );
                        if (isSingleDevice) {
                          return (
                            <span
                              key={String(name)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '2px 0',
                              }}
                            >
                              {swatch}
                              {name}
                            </span>
                          );
                        }
                        return (
                          <button
                            key={String(name)}
                            type="button"
                            aria-pressed={!hidden}
                            title={hidden ? `Show measurements from ${name}` : `Hide measurements from ${name}`}
                            onClick={() => handleLegendClick(name)}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '2px 0',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              fontSize: 'inherit',
                              fontWeight: 'inherit',
                              color: 'inherit',
                              fontFamily: 'inherit',
                              opacity: hidden ? 0.45 : 1,
                              textDecoration: hidden ? 'line-through' : 'none',
                            }}
                          >
                            {swatch}
                            {name}
                          </button>
                        );
                      })}
                    </div>
                  );
                }}
              />
            )}
            <Tooltip contentStyle={{ backgroundColor: 'white' }} content={<BloodPressureTooltip />} />
            <Line type="linear" activeDot={false} dataKey="systolic" stroke="transparent" />
            <Line type="linear" activeDot={false} dataKey="diastolic" stroke="transparent" />
            {vitals?.systolicUpperLimit && vitals?.systolicUpperLimit !== 0 &&
              <>
                <ReferenceLine y={vitals?.systolicUpperLimit} stroke="#CA3B3B" strokeDasharray="3 3" />
                <ReferenceArea y1={domain[1]} y2={vitals?.systolicUpperLimit} fill="rgba(235, 182, 182, 0.2)" stroke="none" strokeDasharray="3 3" />
              </>
            }
            {vitals?.systolicLowerLimit && vitals?.systolicLowerLimit !== 0 &&
              <>
                <ReferenceLine y={vitals?.systolicLowerLimit} stroke="#CA3B3B" strokeDasharray="3 3" />
                <ReferenceArea y1={vitals?.systolicLowerLimit} y2={vitals?.diastolicUpperLimit} fill="rgba(235, 182, 182, 0.2)" stroke="none" strokeDasharray="3 3" />
              </>
            }
            {vitals?.diastolicUpperLimit && vitals?.diastolicUpperLimit !== 0 &&
              <>
                <ReferenceLine y={vitals?.diastolicUpperLimit} stroke="#CA3B3B" strokeDasharray="3 3" />
                <ReferenceArea y1={vitals?.systolicLowerLimit} y2={vitals?.diastolicUpperLimit} fill="rgba(235, 182, 182, 0.2)" stroke="none" strokeDasharray="3 3" />
              </>
            }
            {vitals?.diastolicLowerLimit && vitals?.diastolicLowerLimit !== 0 &&
              <>
                <ReferenceLine y={vitals?.diastolicLowerLimit} stroke="#CA3B3B" strokeDasharray="3 3" />
                <ReferenceArea y1={vitals?.diastolicLowerLimit} y2={domain[0]} fill="rgba(235, 182, 182, 0.2)" stroke="none" strokeDasharray="3 3" />
              </>
            }
            {calibrationReferenceX && (
              <ReferenceLine
                x={calibrationReferenceX}
                stroke="#b8860b"
                strokeWidth={2}
                strokeDasharray="4 4"
                label={renderCalibrationLabel}
              />
            )}
            <Bar dataKey="bpRange" barSize={BP_BAR_SIZE} shape={<BpRangeBar />} isAnimationActive={false} />
            <Bar dataKey="alertY" barSize={ALERT_BAR_SIZE} shape={<BarWithIcon />}>
              {sortedDataWithAlerts.map((entry, index) => {
                const alert = entry.alert;
                const alertColors = getAlertColors(alert?.alert_color ?? '')
                let fillColor = alertColors.color;
                return <Cell key={`cell-${index}`} fill={fillColor} />;
              })}
            </Bar>
            {averageGrouped &&
              <>
                <Customized component={CustomizedMinMaxRectangle} />
                <Scatter
                  dataKey={`diastolic-range.min`}
                  activeDot={false}
                  shape={<ScatterDot />}
                />
                <Scatter
                  dataKey={`systolic-range.max`}
                  activeDot={false}
                  shape={<ScatterDot />}
                />
              </>

            }
          </ComposedChart >
        </ResponsiveContainer>
      </div>
      <PerinHealthModal
        modalId={"BpInitiate"}
        handleButtonClick={handleDialogButtonClick}
      />
    </div>
  );
});

export default BloodPressureCardView;
